import * as React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Client } from '../../models';
import moment from 'moment';
import Storage from '@aws-amplify/storage';
import { inject, observer } from 'mobx-react';
import { UserProfileUIStore } from '../../stores/user-profile-ui-store';
import { avoidEmptyFinalSlice } from '../../utils/html2pdfFinalSlice';
import { toImageDataUrl } from '../../utils/imageDataUrl';

const logo = require('../../assets/imgs/logo.png'); // fallback asset (kept for getLogoUrl)
const html2pdf = require('html2pdf.js');

Storage.configure({
  AWSS3: {
    bucket: 'labsmarts-next',
    region: 'us-east-1',
  },
});

type Props = {
  show: boolean;
  title: string;
  client: Client;
  collectionDate: Date | string;
  elementId: string;
  onHide: () => void;
  userProfileUIStore?: UserProfileUIStore;
  preloadedLogoDataUrl?: string | null; // Optional: pre-loaded logo data URL from parent
};

type State = {
  show: boolean;
  logoDataUrl: string | null; // preloaded header logo (custom or default)
  footerBrandDataUrl: string | null; // preloaded footer brand logo
  isPdfReady: boolean; // a PDF exists and Export will save it
  preparedPdf: any | null; // Pre-generated PDF object ready for save
  // Why preparation failed, shown in place of the Export button. Preparation
  // used to fail by setting isPdfReady true with no PDF behind it, so the button
  // came back, the click fell through to an on-demand retry, that retry hit the
  // same failure, and the practitioner got "Preparing your report..." and a
  // button, over and over, with the reason only in the console.
  prepareError: string | null;
};

@inject('userProfileUIStore')
@observer
export class PDFModal extends React.Component<Props, {}> {
  state: State = {
    show: this.props.show,
    logoDataUrl: null,
    footerBrandDataUrl: null,
    isPdfReady: false,
    preparedPdf: null,
    prepareError: null,
  };

  /** What to show a practitioner about a thrown error. */
  private static messageOf = (error: any): string => {
    const text = String(error?.message || error || '').trim();
    return text || 'Unknown error.';
  };

  componentWillReceiveProps(nextProps: Props) {
    this.setState({ show: nextProps.show });
  }

  // Prepare all PDF assets in advance
  private preparePdfAssets = async (): Promise<void> => {
    // Every open prepares its own PDF. Without this reset the flags survive the
    // close, so the second open renders an enabled "Export to PDF" button over
    // the PREVIOUS report — click it before this pass finishes and the file that
    // saves is the last client's.
    this.setState({ isPdfReady: false, preparedPdf: null, prepareError: null });

    try {
      // The profile is the source of truth for the logo at export time. The
      // parent's preloaded data URL is resolved when the comparison MOUNTS,
      // which on a fresh page load is before the profile has finished loading —
      // so it is often the bundled LabSmarts mark, and preferring it printed
      // that mark on a practitioner's branded report. It stays as the fallback
      // for a resolution that fails outright.
      let logoDataUrl: string | null = null;
      try {
        logoDataUrl = await this.resolveHeaderLogoDataUrl();
      } catch {
        logoDataUrl = null;
      }
      if (!logoDataUrl) {
        logoDataUrl = this.props.preloadedLogoDataUrl || null;
      }

      const footerBrandDataUrl = await this.resolveFooterBrandDataUrl();

      console.log(
        '[PDFModal] logoDataUrl:',
        logoDataUrl ? `${logoDataUrl.substring(0, 50)}...` : 'null',
      );
      console.log(
        '[PDFModal] preloadedLogoDataUrl:',
        this.props.preloadedLogoDataUrl
          ? `${this.props.preloadedLogoDataUrl.substring(0, 50)}...`
          : 'null',
      );

      this.setState({
        logoDataUrl,
        footerBrandDataUrl,
      });

      // Update the logo in DOM so html2canvas picks up a data URL (works for all users)
      const img = document.getElementById('logo') as HTMLImageElement | null;
      if (img && logoDataUrl) {
        img.src = logoDataUrl;
      }

      // Now prepare the actual PDF
      await this.preparePdf(logoDataUrl, footerBrandDataUrl);
    } catch (error) {
      console.error('Error preparing PDF assets:', error);
      this.setState({ prepareError: PDFModal.messageOf(error) });
    }
  };

  // Prepare the actual PDF object in advance
  private preparePdf = async (
    logoDataUrl: string | null,
    footerBrandDataUrl: string,
  ): Promise<void> => {
    // Which half of the build we are in, and what the capture came to, so a
    // thrown message names its own origin. jsPDF's "Supplied Data is not a valid
    // base64-String" can come from html2pdf slicing the canvas into pages OR
    // from the footer brand mark, and the two are unrelated.
    let stage = 'setup';
    let capture = 'not captured';
    try {
      const { client, collectionDate, elementId } = this.props;

      const firstInitialOfFirstName = client.firstName.charAt(0);
      const lastName = client.lastName;
      const formattedCollectionDate =
        moment(collectionDate).format('YYYY-MM-DD');
      const filename = `${lastName}_${firstInitialOfFirstName}-LabSmarts_Bloodwork_Comparison_${formattedCollectionDate}.pdf`;

      const comparisonPanel = document.getElementById(elementId);

      if (!comparisonPanel) {
        console.error(`PDF comparison panel not found: #${elementId}`);
        this.setState({
          prepareError:
            'The report content was not found on the page. Close this window and export again.',
        });
        return;
      }

      const baseOpt = {
        margin: [3, 5, 10, 5],
        filename,
        image: { type: 'jpeg', quality: 1 },
        // See bloodwork-analysis-pdf.tsx: html2pdf's link annotations fragment
        // per text-wrap rect and land on mis-computed pages, so they never
        // worked, and they read to AV heuristics as a scam-document link mat.
        enableLinks: false,
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          scrollX: 0,
          scrollY: 0,
          onclone: (doc: Document) => {
            const img = doc.getElementById('logo') as HTMLImageElement | null;
            if (img && logoDataUrl) {
              img.src = logoDataUrl;
            }
          },
        },
        jsPDF: { orientation: 'l' },
        pagebreak: { mode: ['css', 'legacy'], avoid: '.disclaimer-panel' },
      };

      // Generate PDF and add footer.
      //
      // The capture is staged rather than run as one chain so the slice
      // arithmetic can be checked between the two halves — see
      // avoidEmptyFinalSlice.
      const worker = (html2pdf() as any).set(baseOpt).from(comparisonPanel);
      stage = 'rendering the page to canvas';
      await worker.toCanvas();
      capture = avoidEmptyFinalSlice(worker, 'PDFModal');

      stage = 'slicing the canvas into pages';
      const preparedPdf = await worker
        .toPdf()
        .get('pdf')
        .then((pdf: any) => {
          stage = 'writing the page footers';
          // See bloodwork-analysis-pdf.tsx: an untitled, unattributed PDF whose
          // pages are all rasterized images is what AVG/Avast score as
          // "PDF:MalwareX-gen [Scam]". Keep both export paths named.
          pdf.setProperties({
            title: `LabSmarts Bloodwork Comparison (${formattedCollectionDate})`,
            subject: 'Blood work comparison report',
            author:
              this.props.userProfileUIStore?.currentUserProfile?.preparedBy ||
              'LabSmarts',
            creator: 'LabSmarts',
          });

          // Only add footer if we have the brand data URL
          if (footerBrandDataUrl) {
            // Keep the logo aspect ratio natural (no squishing).
            //
            // getImageProperties throws on anything jsPDF cannot read as an
            // image, and it used to throw from here straight out of the whole
            // export — a footer mark nobody would miss taking the report with
            // it. A brand mark that will not read is dropped instead, and the
            // footer's "Prepared for / Collection Date / Prepared by" line,
            // which is the part a client reads, still prints on every page.
            let props: any = null;
            try {
              props = pdf.getImageProperties(footerBrandDataUrl);
            } catch (brandError) {
              console.error(
                '[PDFModal] footer brand mark unreadable, printing the footer without it:',
                brandError,
              );
            }
            const naturalRatio = props ? props.width / props.height : 0;

            const footerText = 'Powered by';
            const footerFontSize = 8;

            // Tweak this size to taste; width will follow the real ratio automatically
            const logoHeight = 3.8;
            const logoWidth = logoHeight * naturalRatio;

            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i);
              const pageWidth = pdf.internal.pageSize.getWidth();
              const pageHeight = pdf.internal.pageSize.getHeight();

              const rightPadding = 8;
              const bottomPadding = 6;
              const gap = 2;
              const baselineY = pageHeight - bottomPadding;

              pdf.setFontSize(footerFontSize);
              const textWidth = props ? pdf.getTextWidth(footerText) : 0;
              const blockWidth = props ? textWidth + gap + logoWidth : 0;
              const startX = pageWidth - rightPadding - blockWidth;

              // --- Left Footer Text ---
              const { userProfileUIStore } = this.props;
              const preparedBy =
                userProfileUIStore?.currentUserProfile?.preparedBy;
              const fullName = `${client.firstName} ${client.lastName}`;

              let leftBase = `Prepared for: ${fullName}  •  Most Recent Collection Date: ${moment(
                collectionDate,
              ).format('MM/DD/YYYY')}`;
              if (preparedBy) {
                leftBase += `  •  Prepared by: ${preparedBy}`;
              }

              const leftPadding = 8;
              // Ensure we don't overlap with the right block
              const maxLeftWidth = startX - leftPadding - 10;

              const fit = (s: string) => {
                if (pdf.getTextWidth(s) <= maxLeftWidth) return s;
                let t = s;
                while (t.length > 4 && pdf.getTextWidth(t + '…') > maxLeftWidth)
                  t = t.slice(0, -1);
                return t + '…';
              };
              const leftText = fit(leftBase);

              pdf.text(leftText, leftPadding, baselineY);
              // ------------------------

              if (props) {
                pdf.text(footerText, startX, baselineY);
                pdf.addImage(
                  footerBrandDataUrl,
                  (props.fileType as any) || 'PNG',
                  startX + textWidth + gap,
                  baselineY - logoHeight + 0.8, // visual baseline alignment
                  logoWidth,
                  logoHeight,
                );
              }
            }
          }
          return pdf;
        });

      this.setState({
        preparedPdf,
        isPdfReady: true,
      });
    } catch (error) {
      console.error(`Error preparing PDF (${stage}; ${capture}):`, error);
      this.setState({
        prepareError: `${PDFModal.messageOf(error)} [${stage}; ${capture}]`,
      });
    }
  };

  componentDidUpdate(prevProps: Props) {
    // When the modal opens, prepare all PDF assets
    if (!prevProps.show && this.props.show) {
      this.preparePdfAssets();
    }
  }

  handleClose = () => {
    this.props.onHide();
  };

  getLogoUrl = (): string => {
    const { userProfileUIStore } = this.props;
    const userProfile = userProfileUIStore?.currentUserProfile;
    return userProfile?.hasCustomLogo() ? userProfile.customLogoUrl : logo;
  };

  // See bloodwork-analysis-pdf.tsx: the fetch-and-encode this used to be could
  // return a string that is not an image, and jsPDF answers that by throwing out
  // of the whole export.
  private toDataUrl = (url: string): Promise<string> => toImageDataUrl(url);

  // Resolve header logo (custom if available; else bundled default) to a data URL
  private resolveHeaderLogoDataUrl = async (): Promise<string> => {
    const bundled =
      (require('../../assets/imgs/logo.png') as any)?.default ||
      (require('../../assets/imgs/logo.png') as any);

    // No custom logo: use bundled default directly, skip all S3 logic
    if (!this.props.userProfileUIStore?.currentUserProfile?.hasCustomLogo()) {
      return await this.toDataUrl(bundled);
    }

    const raw = this.getLogoUrl() as any;
    const candidate = (raw?.default ?? raw) as string | undefined;

    // 1) If getLogoUrl() gives an http(s) URL, try it directly
    if (candidate && /^https?:\/\//i.test(candidate)) {
      try {
        return await this.toDataUrl(candidate);
      } catch {
        // fall through to S3/default
      }
    }

    // 2) Try S3 (user-logos/<filename from getLogoUrl()>)
    try {
      const key = (candidate || '').toString().split('/').pop();
      if (key) {
        const s3Url = await Storage.get(`user-logos/${key}`);
        return await this.toDataUrl(s3Url as string);
      }
    } catch {
      // fall through to default
    }

    // 3) Fallback: bundled default asset
    return await this.toDataUrl(bundled);
  };

  // Footer brand mark: use bundled default (keeps brand consistent), as a data URL
  private resolveFooterBrandDataUrl = async (): Promise<string> => {
    const brandUrl =
      (require('../../assets/imgs/logo.png') as any)?.default ||
      (require('../../assets/imgs/logo.png') as any);
    return await this.toDataUrl(brandUrl);
  };

  export = async () => {
    const { preparedPdf, isPdfReady } = this.state;
    const { client, collectionDate } = this.props;

    const firstInitialOfFirstName = client.firstName.charAt(0);
    const lastName = client.lastName;
    const formattedCollectionDate = moment(collectionDate).format('YYYY-MM-DD');
    const filename = `${lastName}_${firstInitialOfFirstName}-LabSmarts_Bloodwork_Comparison_${formattedCollectionDate}.pdf`;

    if (preparedPdf && isPdfReady) {
      preparedPdf.save(filename);
      return;
    }

    // Export SAVES; it does not prepare. Preparing here is what looped: the
    // click re-ran a preparation that had already failed, so the practitioner
    // got "Preparing your report...", then the button back, then nothing —
    // forever. Retrying is now a deliberate button beside the reason it failed.
    console.error('Export clicked with no prepared PDF');
    this.setState({
      prepareError:
        this.state.prepareError || 'The report is not ready to export yet.',
    });
  };

  render() {
    if (!this.state.show) return null;
    const { title } = this.props;
    const { isPdfReady, prepareError } = this.state;

    return (
      <Modal size="xl" show={this.state.show} onHide={this.handleClose}>
        <Modal.Header
          closeButton
          style={{ backgroundColor: '#337ab7', color: '#fff' }}
        >
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Mirrors the status row in bloodwork-analysis-pdf.tsx. The button
              used to render at opacity 0 while the PDF was being prepared, so
              its "Preparing PDF..." label was invisible and the top right of
              the modal simply looked empty — a practitioner waiting on a large
              comparison had no way to tell preparation from a missing button.
              The inline opacity on the button stays: #pdf-export-button is set
              to opacity 0 in containers/pdf.css, which is bundled globally. */}
          <div className="text-right">
            {isPdfReady ? (
              <Button
                id="pdf-export-button"
                style={{ opacity: 1 }}
                onClick={this.export}
              >
                Export to PDF
              </Button>
            ) : prepareError ? (
              <span>
                <span
                  className="text-danger mr-3"
                  style={{ fontSize: 15 }}
                  data-testid="pdf-prepare-error"
                >
                  This report could not be prepared: {prepareError}
                </span>
                <Button
                  variant="outline-secondary"
                  onClick={() => this.preparePdfAssets()}
                >
                  Try again
                </Button>
              </span>
            ) : (
              <span
                style={{
                  color: '#766d66',
                  fontStyle: 'italic',
                  fontSize: 15,
                }}
              >
                Preparing your report... the Export to PDF button will appear
                here in a few seconds.
              </span>
            )}
          </div>
          {this.props.children}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.handleClose}>Close</Button>
          {isPdfReady && <Button onClick={this.export}>Export to PDF</Button>}
        </Modal.Footer>
      </Modal>
    );
  }
}
